import React from "react";
import { Input, Select, Radio, Tooltip, Button, Tag } from "antd";
import {
  DeleteOutlined,
  StarFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import FormField from "./FormField";
import KeywordSelect from "./KeywordSelect";
import { INTERIOR_SUB_CATEGORIES, IMAGE_CATEGORY_WITH_SUB } from "../constants";
import { imageNeedsSubCategory } from "../utils";

const { Option } = Select;

/**
 * One uploaded image plus its tagging fields.
 *
 * A new image arrives carrying the details of the last one the admin filled
 * in, so tagging four photos of the same project is four small edits rather
 * than four full forms. The moment this card is edited it stops inheriting,
 * so a later change upstream never overwrites work already done here.
 */
const ImageCard = ({
  item,
  index,
  total,
  masters,
  errors = {},
  onChange,
  onRemove,
  onMove,
  onCopyFromPrevious,
}) => {
  const showSubCategory = imageNeedsSubCategory(item.category);

  const handleCategoryChange = (value) => {
    // Clearing the dependent field keeps a stale interior sub-category from
    // being submitted after the category is switched to, say, Floorplan.
    onChange(item.uid, {
      category: value || "",
      subCategory: value === IMAGE_CATEGORY_WITH_SUB ? item.subCategory : "",
    });
  };

  return (
    <div className={`mmh-image-row ${item.isDp ? "is-dp" : ""} ${item.touched ? "" : "is-inherited"}`}>
      <div className="mmh-image-preview-col">
        <div className="mmh-image-thumb">
          <img src={item.preview} alt={item.title || `Product image ${index + 1}`} />

          {item.isDp && (
            <span className="mmh-image-dp-badge">
              <StarFilled /> DP
            </span>
          )}

          <span className="mmh-image-index">{index + 1}</span>

          <Tooltip title="Remove this image">
            <button
              type="button"
              className="mmh-image-remove"
              aria-label={`Remove image ${index + 1}`}
              onClick={() => onRemove(item.uid)}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>

        <Radio value={item.uid} className="mmh-image-dp-radio">
          {item.isDp ? "Display picture" : "Set as DP"}
        </Radio>

        <div className="mmh-image-order">
          <Tooltip title="Move up">
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={index === 0}
              onClick={() => onMove(index, index - 1)}
              aria-label={`Move image ${index + 1} up`}
            />
          </Tooltip>
          <Tooltip title="Move down">
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={index === total - 1}
              onClick={() => onMove(index, index + 1)}
              aria-label={`Move image ${index + 1} down`}
            />
          </Tooltip>
          {index > 0 && (
            <Tooltip title="Copy the details of the image above">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => onCopyFromPrevious(item.uid)}
                aria-label={`Copy details from image ${index}`}
              />
            </Tooltip>
          )}
        </div>

        {item.fileName && <div className="mmh-image-filename">{item.fileName}</div>}
      </div>

      <div className="mmh-image-fields-col">
        {!item.touched && index > 0 && (
          <Tag color="gold" className="mmh-inherited-tag">
            Copied from the image above — edit anything that differs
          </Tag>
        )}

        <div className="mmh-grid-2">
          <FormField label="Image Name" required error={errors.title}>
            <Input
              placeholder="e.g. Ground Floor Plan"
              value={item.title}
              onChange={(e) => onChange(item.uid, { title: e.target.value })}
            />
          </FormField>

          <FormField label="Select Category" required error={errors.category}>
            <Select
              value={item.category || undefined}
              placeholder="Select Category"
              onChange={handleCategoryChange}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {masters.imageCategories.map((o) => (
                <Option value={o} key={o}>{o}</Option>
              ))}
            </Select>
          </FormField>

          {showSubCategory && (
            <FormField label="Select Sub Category" required error={errors.subCategory}>
              <Select
                value={item.subCategory || undefined}
                placeholder="Select Sub Category"
                onChange={(v) => onChange(item.uid, { subCategory: v || "" })}
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {INTERIOR_SUB_CATEGORIES.map((o) => (
                  <Option value={o} key={o}>{o}</Option>
                ))}
              </Select>
            </FormField>
          )}

          <FormField label="Other Category" hint="If you did not find your category above">
            <Input
              placeholder="Other Category"
              value={item.otherCategory}
              onChange={(e) => onChange(item.uid, { otherCategory: e.target.value })}
            />
          </FormField>

          <FormField label="Select Style" required error={errors.style}>
            <Select
              value={item.style || undefined}
              placeholder="Select Style"
              onChange={(v) => onChange(item.uid, { style: v || "" })}
              showSearch
              allowClear
              optionFilterProp="children"
            >
              {masters.styles.map((s) => (
                <Option value={s.name} key={s.id ?? s.name}>{s.name}</Option>
              ))}
            </Select>
          </FormField>

          <FormField
            label="Search Keywords"
            className="mmh-image-keyword-field"
            hint="Pick from suggestions or type a new keyword and press Enter"
          >
            <KeywordSelect
              value={item.keywords}
              onChange={(v) => onChange(item.uid, { keywords: v })}
            />
          </FormField>

          <FormField label="Description" className="mmh-image-desc-field">
            <Input.TextArea
              rows={2}
              placeholder="Description"
              value={item.description}
              onChange={(e) => onChange(item.uid, { description: e.target.value })}
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

export default ImageCard;
